/**
 * Adapters between DiagnosisForm's Dx v2 body shape (camelCase, sent to
 * api-diagnosis-v2-service today) and the transcript's captured-item
 * payload shape (snake_case, matches what the Python agent emits and
 * what Go's SaveSection fan-out reads on Save & next).
 *
 * DiagnosisForm is now the one editor used by both the standalone
 * Diagnosis tab and the AI transcript modal. In transcript mode the
 * caller passes `onSave` and receives the body in Dx v2 shape; these
 * helpers convert it to the captured-item shape before POST /captured
 * / decideCaptured. Keeps the form component ignorant of any
 * transcript-specific vocabulary.
 *
 *   Dx v2 add body        → captured "add" payload
 *   {diagnosis, diagnosisCode:{icdCode, label?}, ongoing, sinceWhen,
 *    description, consultedDoctor, complaintIds, medicineRefs, labRefs}
 *      →
 *   {diagnosis, icd_code, icd_description, treatment_status,
 *    since_date, description, consulted_doctor}
 *
 *   Dx v2 update body     → captured "update" payload
 *   {ongoing, sinceWhen, description, consultedDoctor, complaintIds,
 *    medicineRefs, labRefs}
 *      →
 *   {diagnosis, icd_code, treatment_status, since_when, description,
 *    consulted_doctor}
 *
 * The update payload additionally carries `diagnosis` + `icd_code`
 * pulled from the Dx v2 record being edited, because the update card
 * doesn't collect them (the ICD/name are read-only in update mode)
 * but the review table + AdaptDiagnosisUpdate on the Go side need
 * them for display + provenance.
 *
 * Linked entities (complaintIds / medicineRefs / labRefs) are
 * intentionally NOT carried onto the captured payload — the transcript
 * flow doesn't touch them (per SECTIONS_SPEC.md, simplification B).
 */

// treatment_status enum used in the captured payload — matches the AI
// tool's args and Go adapter's parser.
const ongoingToStatus = (ongoing) => (ongoing ? "ongoing" : "completed");

// ISO datetime like "2026-08-19T00:00:00.000Z" → date-only "2026-08-19".
// Same helper CC uses. Go's adapter toISOUTC upgrades a plain date back
// to RFC3339 on the write path.
const toIsoDate = (iso) => (typeof iso === "string" && iso ? iso.slice(0, 10) : "");

/**
 * Dx v2 add body → captured "add" payload.
 */
export function bodyToCapturedAdd(body) {
  const cc = body.diagnosisCode || {};
  return {
    diagnosis:        body.diagnosis || "",
    icd_code:         cc.icdCode || "",
    icd_description:  cc.label || cc.icdDescription || "",
    treatment_status: ongoingToStatus(!!body.ongoing),
    since_date:       toIsoDate(body.sinceWhen),
    description:      body.description || "",
    consulted_doctor: !!body.consultedDoctor,
  };
}

/**
 * Dx v2 update body → captured "update" payload. `context` carries the
 * diagnosis name + ICD code from the Dx v2 record being edited (or from
 * the staged captured payload for a re-edit), pulled through so the
 * review table can render "Update · <name>" without a second lookup
 * and so the Go adapter has the ICD for its own writes.
 */
export function bodyToCapturedUpdate(body, context) {
  const ctx = context || {};
  const cc = ctx.diagnosisCode || {};
  return {
    diagnosis:        ctx.diagnosis || "",
    icd_code:         cc.icdCode || ctx.icd_code || "",
    treatment_status: ongoingToStatus(!!body.ongoing),
    since_when:       toIsoDate(body.sinceWhen),
    description:      body.description || "",
    consulted_doctor: !!body.consultedDoctor,
  };
}
